import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [status, setStatus] = useState('checking'); // checking | valid | invalid | done
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Verify the token as soon as the page loads. Prevents wasted keystrokes
  // if the link is broken/expired; also removes the token from the URL bar
  // once we've captured it into state.
  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    (async () => {
      try {
        const r = await fetch(`${API}/api/auth/reset-password/verify?token=${encodeURIComponent(token)}`);
        const d = await r.json();
        setStatus(d.valid ? 'valid' : 'invalid');
        if (d.valid) {
          // Scrub the token from the visible URL for defence-in-depth
          // (still readable via URLSearchParams synchronously above).
          window.history.replaceState({}, '', '/reset-password');
        }
      } catch {
        setStatus('invalid');
      }
    })();
  }, [token]);

  const passwordError = () => {
    if (pw1.length < 8) return t('auth.resetPassword.errTooShort');
    if (!/[A-Za-z]/.test(pw1) || !/[0-9]/.test(pw1)) return t('auth.resetPassword.errComposition');
    if (pw1 !== pw2) return t('auth.resetPassword.errMismatch');
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError(null);
    const err = passwordError();
    if (err) { setError(err); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: pw1 }),
      });
      const d = await r.json();
      if (!r.ok) {
        if (d.detail === 'invalid_or_expired') {
          setStatus('invalid');
        } else {
          setError(t('auth.resetPassword.errGeneric'));
        }
        return;
      }
      setStatus('done');
    } catch {
      setError(t('auth.resetPassword.errGeneric'));
    } finally {
      setLoading(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50">
        <Card className="w-full max-w-md" data-testid="reset-invalid-card">
          <CardHeader className="text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 flex items-center justify-center mb-2">
              <XCircle className="w-6 h-6 text-rose-600" />
            </div>
            <CardTitle data-testid="reset-invalid-title">
              {t('auth.resetPassword.invalidTitle')}
            </CardTitle>
            <CardDescription>
              {t('auth.resetPassword.invalidSubtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/forgot-password')}
                    data-testid="reset-request-new-link">
              {t('auth.resetPassword.requestNew')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50">
        <Card className="w-full max-w-md" data-testid="reset-done-card">
          <CardHeader className="text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <CardTitle data-testid="reset-done-title">
              {t('auth.resetPassword.successTitle')}
            </CardTitle>
            <CardDescription>
              {t('auth.resetPassword.successSubtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button onClick={() => navigate('/login')} data-testid="reset-continue-to-login">
              {t('auth.resetPassword.continueToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // status === 'valid' → render the new-password form
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-slate-50">
      <Card className="w-full max-w-md" data-testid="reset-form-card">
        <CardHeader className="text-center">
          <CardTitle data-testid="reset-form-title">
            {t('auth.resetPassword.title')}
          </CardTitle>
          <CardDescription>
            {t('auth.resetPassword.policyHint')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="rp-pw1">{t('auth.resetPassword.newPassword')}</Label>
              <div className="relative">
                <Input
                  id="rp-pw1"
                  type={show ? 'text' : 'password'}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  autoFocus
                  autoComplete="new-password"
                  aria-invalid={!!error}
                  data-testid="reset-new-password-input"
                  required
                />
                <button type="button"
                        onClick={() => setShow(s => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                        aria-label={show ? t('auth.resetPassword.hidePassword') : t('auth.resetPassword.showPassword')}
                        data-testid="reset-toggle-visibility">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rp-pw2">{t('auth.resetPassword.confirmPassword')}</Label>
              <Input
                id="rp-pw2"
                type={show ? 'text' : 'password'}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                autoComplete="new-password"
                aria-invalid={!!error}
                data-testid="reset-confirm-password-input"
                required
              />
              {error && (
                <p role="alert" className="text-sm text-rose-600" data-testid="reset-form-error">
                  {error}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading || !pw1 || !pw2}
                    data-testid="reset-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('auth.resetPassword.submit')}
            </Button>
            <div className="text-center">
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900">
                {t('auth.forgotPassword.backToLogin')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
